import React, { useEffect, useMemo, useState } from 'react';
import { formatItemCount } from '../utils/labels';
import './LocationTree.css';

// rows come from GET /api/locations/all: { id, name, parentLocationId, path }
function groupByParent(rows) {
  const children = new Map();
  for (const row of rows) {
    const key = row.parentLocationId || '';
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(row);
  }
  for (const list of children.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  return children;
}

// The excluded places and everything beneath them cannot be chosen.
function blockedSet(children, excludeIds) {
  const blocked = new Set();
  const visit = (id) => {
    blocked.add(id);
    (children.get(id) || []).forEach((child) => visit(child.id));
  };
  excludeIds.forEach(visit);
  return blocked;
}

export function useLocations() {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/locations/all')
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setLocations(data);
      })
      .catch((error) => console.error('Error fetching locations:', error));
    return () => {
      cancelled = true;
    };
  }, []);
  return locations;
}

// A collapsible tree in a modal. Choosing a row calls onSelect(id, path) ('' for the top option).
export function LocationTreeModal({ title, topLabel, locations, excludeIds = [], selectedId = '', onSelect, onClose }) {
  const children = useMemo(() => groupByParent(locations), [locations]);
  const byId = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);
  const blocked = useMemo(() => blockedSet(children, excludeIds), [children, excludeIds]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  // Open the tree down to the current choice
  useEffect(() => {
    const open = new Set();
    for (let cur = byId.get(selectedId); cur && cur.parentLocationId; cur = byId.get(cur.parentLocationId)) {
      open.add(cur.parentLocationId);
    }
    setExpanded(open);
  }, [byId, selectedId]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderRows = (parentKey, depth) =>
    (children.get(parentKey) || []).map((loc) => {
      const kids = children.get(loc.id) || [];
      const isOpen = expanded.has(loc.id);
      const disabled = blocked.has(loc.id);
      return (
        <React.Fragment key={loc.id}>
          <div className="tree-row" style={{ paddingLeft: 8 + depth * 18 }}>
            {kids.length > 0 ? (
              <button
                type="button"
                className="tree-toggle"
                onClick={() => toggle(loc.id)}
                aria-label={isOpen ? `Collapse ${loc.name}` : `Expand ${loc.name}`}
              >
                {isOpen ? '▾' : '▸'}
              </button>
            ) : (
              <span className="tree-toggle-spacer" />
            )}
            <button
              type="button"
              className={`tree-name${loc.id === selectedId ? ' selected' : ''}`}
              disabled={disabled}
              onClick={() => onSelect(loc.id, loc.path || loc.name)}
            >
              <span className="tree-label">{loc.name}</span>
              <span className="tree-count">{formatItemCount(loc.itemCount)}</span>
            </button>
          </div>
          {isOpen && renderRows(loc.id, depth + 1)}
        </React.Fragment>
      );
    });

  const term = search.trim().toLowerCase();
  const matches = term
    ? locations
        .filter((l) => (l.path || l.name).toLowerCase().includes(term))
        .sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name))
    : [];

  return (
    <div className="tree-overlay" role="dialog" aria-modal="true">
      <div className="tree-box">
        <div className="tree-header">
          <h3>{title}</h3>
          <button type="button" className="btn btn-secondary btn-small" onClick={onClose}>
            Cancel
          </button>
        </div>
        <input
          type="text"
          className="tree-search"
          placeholder="Search by name or path..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="tree-list">
          {term ? (
            matches.length === 0 ? (
              <p className="tree-empty">No match</p>
            ) : (
              matches.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  className={`tree-match${loc.id === selectedId ? ' selected' : ''}`}
                  disabled={blocked.has(loc.id)}
                  onClick={() => onSelect(loc.id, loc.path || loc.name)}
                >
                  <span className="tree-label">{loc.path || loc.name}</span>
                  <span className="tree-count">{formatItemCount(loc.itemCount)}</span>
                </button>
              ))
            )
          ) : (
            <>
              {topLabel && (
                <div className="tree-row">
                  <span className="tree-toggle-spacer" />
                  <button
                    type="button"
                    className={`tree-name tree-top${selectedId === '' ? ' selected' : ''}`}
                    onClick={() => onSelect('', topLabel)}
                  >
                    {topLabel}
                  </button>
                </div>
              )}
              {renderRows('', 0)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// A form field that shows the chosen place and opens the tree to change it.
function LocationPicker({ value, onChange, topLabel, excludeIds = [], title = 'Choose a place' }) {
  const locations = useLocations();
  const [open, setOpen] = useState(false);
  const chosen = locations.find((l) => l.id === value);

  return (
    <>
      <button type="button" className="location-picker-field" onClick={() => setOpen(true)}>
        <span>{value ? (chosen ? chosen.path || chosen.name : 'Loading...') : topLabel}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <LocationTreeModal
          title={title}
          topLabel={topLabel}
          locations={locations}
          excludeIds={excludeIds}
          selectedId={value || ''}
          onSelect={(id) => {
            onChange(id);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default LocationPicker;
